import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Radio, Tooltip } from '@material-ui/core';
import { observer } from 'mobx-react';
import React from 'react';
import { IEntity, IHarmonization } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { mg } from '../../../mg/mg';
import { IBooleanStateAgg, useBooleanStateAgg } from '../../../mg/useStateAgg';
import { commSvc } from '../../../services/CommSvc';
import { toasterSvc } from '../../../services/ToasterSvc';
import { IDataviewStore } from '../../../stores/DataviewStore';
import { SupplierItemExtended } from './SupplierItemExtended';
import { useHarmoState } from './useHarmoState';

const useTooltipStyles = makeStyles({
	tooltip: {
		backgroundColor: '#FFF',
		color: '#F00',
		maxHeight: 50,
		fontSize: 14,
		border: '1px solid #F88',
	},
	arrow: {
		color: '#F00',
	},
});

const useStyles = makeStyles({
	harmoPrimarySecondary: {
	},
	bigBox: {
		'& svg': {
			fontSize: '120%'
		}
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr 1fr',
		'& > div': {
			padding: 10,
			borderBottom: '1px solid #DDD',
		},
	},
	header: {
		fontWeight: 'bold',
	},
});

type ISendParam = Omit<Omit<IHarmonization, 'ownerId'>, keyof IEntity> & {id: number | undefined};

interface IRelinkDialogProps {
	openAgg: IBooleanStateAgg
	oldMasterItem: SupplierItemExtended
	supplierItem: SupplierItemExtended
	param: ISendParam
	sendFn(param: ISendParam) : Promise<unknown>
}

const RelinkDialog = observer(function(props: IRelinkDialogProps) {
	const { openAgg, param, sendFn, oldMasterItem, supplierItem } = props;
	const classes = useStyles();
	function relink(state:IHarmonization['state']) {
		return () => sendFn({...param, state}).finally(openAgg.falseHandler);
	}
	const hideUnknown = (x: string|number) => x===0 || x==='?' ? '' : x;
	return <Dialog open={openAgg.value} onClose={openAgg.falseHandler}>
		<DialogTitle>{transl('unlinkOrRelink')}</DialogTitle>
		<DialogContent>
			<div className={classes.grid}>
				<div></div>
				<div className={classes.header}>{transl('vendorItem')}</div>
				<div className={classes.header}>{transl('wholesalerItem')}</div>
				{
					['name', 'packaging', 'itemNo', 'eancode', 'bundle', 'amount', 'unit', 'id'].map((s) => <React.Fragment key={s}>
						<div className={classes.header}>{transl(s)}</div>
						<div>{oldMasterItem[s]}</div>
						<div>{hideUnknown(supplierItem[s])}</div>
					</React.Fragment>)
				}
			</div>
		</DialogContent>
		<DialogActions>
			<Button onClick={openAgg.falseHandler}>{transl('cancel')}</Button>
			<Button color="secondary" onClick={relink('DELETED')}>{transl('relink-deleted')}</Button>
			<Button color="primary" onClick={relink('PRIMARY')}>{transl('relink-primary')}</Button>
			<Button color="primary" onClick={relink('SECONDARY')}>{transl('relink-secondary')}</Button>
		</DialogActions>
	</Dialog>;
});

export const HarmoPrimarySecondary = observer(function(props: {entity: IEntity, state: IDataviewStore}) {
	const supplierItem = props.entity as SupplierItemExtended;
	const classes = useStyles();
	const tooltipClasses = useTooltipStyles();
	const relinkOpenAgg = useBooleanStateAgg();
	const tooltipOpenAgg = useBooleanStateAgg();
	const busyAgg = useBooleanStateAgg();
	mg.assertTrue(supplierItem instanceof SupplierItemExtended);
	const { masterItem, harmonizationRepo, slaveSupplierAgg, masterSupplierItemExtendedRepo } = useHarmoState();
	const harmonization = supplierItem.harmonization;
	const state = supplierItem.state;
	const conflictingItem = state==='DELETED' || harmonization?.masterItemId===masterItem?.id ? undefined : masterSupplierItemExtendedRepo.byId(harmonization?.masterItemId);
	const param: ISendParam = {
		id: supplierItem.id,
		state: 'DELETED',
		masterItemId: masterItem?.id as number,
		supplierId: slaveSupplierAgg.id,
	};
	function sendFn(param: ISendParam) : Promise<unknown> {
		if (busyAgg.value) {
			toasterSvc.warning('pleaseWait');
			return Promise.resolve();
		}
		busyAgg.trueHandler();
		const responsePromise = commSvc.doPost('PutHarmonization', param);
		return harmonizationRepo.handleResponse(responsePromise, 'relinked-' + param.state.toLowerCase())
			.finally(busyAgg.falseHandler);
	}
	function onRadioClick() {
		sendFn({...param, state: state==='PRIMARY' ? 'SECONDARY' : 'PRIMARY'});
	}
	function onCheckboxClick() {
		sendFn({...param, state: state!=='DELETED' ? 'DELETED' : 'SECONDARY'});
	}
	const tooltipTitle = conflictingItem ? conflictingItem.name + ' ' + conflictingItem.packaging
		: '';
	return <Tooltip arrow classes={tooltipClasses} placement="left" enterDelay={100} className={classes.harmoPrimarySecondary}
		title={tooltipTitle}
		open={tooltipOpenAgg.value && !relinkOpenAgg.value}
		onClose={tooltipOpenAgg.falseHandler}
		onOpen={tooltipOpenAgg.trueHandler}
	>{
			supplierItem===masterItem ? <div>
			</div>
				: conflictingItem ?
					<Button variant="outlined" color="secondary"
						onClick={relinkOpenAgg.trueHandler}
					>
						{transl('linkedElsewhere')}
						<RelinkDialog
							openAgg={relinkOpenAgg}
							oldMasterItem={conflictingItem}
							supplierItem={supplierItem}
							param={param}
							sendFn={sendFn}
						/>
					</Button>
					:
					<div>
						<Radio color="primary"
							checked={state==='PRIMARY'}
							onClick={onRadioClick}
						/>
						<Checkbox color="primary"
							checked={state!=='DELETED'}
							onClick={onCheckboxClick}
						/>
					</div>
		}
	</Tooltip>;
});
